import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { CalendarIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { API_BASE } from '../../lib/config';

const API = `${API_BASE}`;

// エラーメッセージ正規化（オブジェクト/配列でも安全に文字列化）
const normalizeError = (err) => {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (err.detail) {
    const d = err.detail;
    if (typeof d === 'string') return d;
    try { return JSON.stringify(d); } catch (_) { /* noop */ }
  }
  try { return JSON.stringify(err); } catch (_) { return String(err); }
};

const RecruitmentScheduleManagement = () => {
  const { user } = useContext(AuthContext);
  const [employerProfile, setEmployerProfile] = useState(null);
  const [recruitmentDates, setRecruitmentDates] = useState([]);
  const [dailyCapacity, setDailyCapacity] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // 直近2ヶ月分の日付生成
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 0; i < 60; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]); // YYYY-MM-DD
    }
    
    return dates;
  };

  const availableDates = generateAvailableDates();

  // 事業者プロフィール取得
  const fetchEmployerProfile = async () => {
    try {
      setLoading(true);
      
      // まず事業者のプロフィール一覧を取得
      const response = await fetch(`${API}/employer-profiles`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const profiles = await response.json();
        // 現在のユーザーのプロフィールを検索
        const myProfile = profiles.find(p => p.employer_id === user.id);
        
        if (myProfile) {
          setEmployerProfile(myProfile);
          setRecruitmentDates(myProfile.recruitment_dates || []);
          setDailyCapacity(myProfile.daily_capacity || {});
        }
      } else {
        let msg = 'プロフィール情報の取得に失敗しました';
        try {
          const data = await response.json();
          msg = normalizeError(data) || msg;
        } catch (_) {}
        setError(msg);
      }
    } catch (err) {
      setError(normalizeError(err) || 'ネットワークエラーが発生しました');
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  // 募集日程の保存
  const saveRecruitmentSchedule = async () => {
    if (!employerProfile) {
      setError('プロフィール情報が見つかりません');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const response = await fetch(`${API}/employer-profiles/${employerProfile.id}/recruitment-schedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          recruitment_dates: recruitmentDates,
          daily_capacity: dailyCapacity
        })
      });

      if (response.ok) {
        setSuccessMessage('募集日程を保存しました');
      } else {
        let msg = '保存に失敗しました';
        try {
          const errorData = await response.json();
          msg = normalizeError(errorData) || msg;
        } catch (_) {}
        setError(msg);
      }
    } catch (err) {
      setError(normalizeError(err) || 'ネットワークエラーが発生しました');
      console.error('Error saving schedule:', err);
    } finally {
      setSaving(false);
    }
  };

  // 募集日の追加
  const addRecruitmentDate = (date) => {
    if (!recruitmentDates.includes(date)) {
      setRecruitmentDates([...recruitmentDates, date].sort());
      if (!dailyCapacity[date]) {
        setDailyCapacity({
          ...dailyCapacity,
          [date]: 1 // デフォルト1名
        });
      }
    }
  };

  // 募集日の削除
  const removeRecruitmentDate = (date) => {
    setRecruitmentDates(recruitmentDates.filter(d => d !== date));
    const newCapacity = { ...dailyCapacity };
    delete newCapacity[date];
    setDailyCapacity(newCapacity);
  };

  // 募集人数の変更
  const updateDailyCapacity = (date, capacity) => {
    setDailyCapacity({
      ...dailyCapacity,
      [date]: Math.max(1, parseInt(capacity) || 1)
    });
  };

  // 日付フォーマット関数
  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayOfWeek = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
    return `${month}/${day}(${dayOfWeek})`;
  };

  useEffect(() => {
    if (user && user.user_type === 'employer') {
      fetchEmployerProfile();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!employerProfile) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-4">募集日程管理</h2>
          <p className="text-gray-500">事業者プロフィールが見つかりません。</p>
          <p className="text-gray-500 text-sm mt-2">まず事業者プロフィールを作成してください。</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <CalendarIcon className="h-6 w-6 mr-2" />
              募集日程管理
            </h2>
            <p className="text-gray-600 mt-1">直近2ヶ月分の募集日程を設定できます</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">事業者名</div>
            <div className="font-medium">{employerProfile.business_name}</div>
          </div>
        </div>
      </div>

      {/* 成功・エラーメッセージ */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{typeof error === 'string' ? error : normalizeError(error)}</p>
        </div>
      )}

      {/* 募集日程設定 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">募集日程の設定</h3>
        
        {/* 日付選択エリア */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            募集する日付を選択してください
          </label>
          <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {availableDates.map((date) => {
                const isSelected = recruitmentDates.includes(date);
                const isToday = date === new Date().toISOString().split('T')[0];
                
                return (
                  <button
                    key={date}
                    onClick={() => isSelected ? removeRecruitmentDate(date) : addRecruitmentDate(date)}
                    className={`p-2 rounded-lg border text-sm transition-colors ${
                      isSelected
                        ? 'bg-blue-500 text-white border-blue-500'
                        : isToday
                        ? 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'
                        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {formatDateDisplay(date)}
                  </button>
                );
              })}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            選択された日付: {recruitmentDates.length}日
          </p>
        </div>

        {/* 選択済み日程一覧 */}
        {recruitmentDates.length > 0 && (
          <div className="mb-6">
            <h4 className="text-md font-medium text-gray-900 mb-3">選択済み募集日程</h4>
            <div className="space-y-3">
              {recruitmentDates.map((date) => (
                <div key={date} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900">
                      {formatDateDisplay(date)}
                    </span>
                    <span className="text-gray-500 text-sm ml-2">
                      ({date})
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center">
                      <label className="text-sm text-gray-600 mr-2">募集人数:</label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={dailyCapacity[date] || 1}
                        onChange={(e) => updateDailyCapacity(date, e.target.value)}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-center text-sm"
                      />
                      <span className="text-sm text-gray-500 ml-1">名</span>
                    </div>
                    
                    <button
                      onClick={() => removeRecruitmentDate(date)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="削除"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 保存ボタン */}
        <div className="flex justify-end">
          <button
            onClick={saveRecruitmentSchedule}
            disabled={saving || recruitmentDates.length === 0}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                保存中...
              </>
            ) : (
              '募集日程を保存'
            )}
          </button>
        </div>
      </div>

      {/* 使用方法の説明 */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h4 className="text-md font-semibold text-blue-900 mb-2"> 使用方法</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li> 募集したい日付をクリックして選択してください</li>
          <li> 各日付の募集人数を設定してください（1〜20名）</li>
          <li> 直近2ヶ月分までの日程を設定できます</li>
          <li> 設定した日程は求職者が日付別に検索できるようになります</li>
          <li> 募集人数に達した場合、自動的に応募受付が停止されます</li>
        </ul>
      </div>
    </div>
  );
};

export default RecruitmentScheduleManagement;